import React, { useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { Card, Button, Modal, Form, message, Space, Spin, Alert } from "antd";
import {
  PlusOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { DrawingManager } from "../../components/DrawingManager";
import { ExistingPolygons } from "../../components/ExistingPolygons";
import { SectionTable } from "./components/SectionTable";
import { SectionForm } from "./components/SectionForm";
import { useSectionManagement } from "./hooks/useSectionManagement";
import { useZoneDetails } from "../../hooks/useZoneDetails";
import PermissionRender from "../../components/PermissionRender";

const SectionPage = () => {
  const { zoneId } = useParams();
  const [form] = Form.useForm();

  // Fetch zone details
  const {
    zoneDetails,
    loading: zoneLoading,
    error: zoneError,
  } = useZoneDetails(zoneId);

  // Section management hook
  const {
    entities,
    currentEntity,
    isModalVisible,
    loading,
    drawingMode,
    editingEntity,
    isEditing,
    zoomToEntity,
    fetchEntities,
    handlePolygonComplete,
    startEditingEntity,
    handlePolygonEdit,
    handleViewEntity,
    cancelEditing,
    handleSave,
    handleModalClose,
    deleteEntity,
    setDrawingMode,
    setIsModalVisible,
    setParentEntity,
  } = useSectionManagement(zoneId, form);

  useEffect(() => {
    if (zoneId) {
      fetchEntities();
    }
  }, [zoneId]);

  useEffect(() => {
    if (zoneDetails) {
      handleViewEntity(zoneDetails);
      setParentEntity(zoneDetails);
    }
  }, [zoneDetails]);

  // Handle section deletion
  const handleDelete = (zoneId) => {
    Modal.confirm({
      title: "Supprimer la zone",
      content:
        "Êtes-vous sûr de vouloir supprimer cette zone ? Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        try {
          await deleteEntity(zoneId);
          message.success("Zone supprimée avec succès");
          fetchEntities();
        } catch (error) {
          message.error("Échec de la suppression de la zone");
        }
      },
    });
  };

  const openEditModal = () => {
    if (editingEntity) {
      form.setFieldsValue({
        name: editingEntity.name,
        description: editingEntity.description,
      });
      setIsModalVisible(true);
    }
  };

  if (zoneLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (zoneError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Error"
          description="Échec du chargement des détails de la contrat"
          type="error"
          showIcon
        />
      </div>
    );
  }
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} region="MA">
      <div style={{ padding: 24 }}>
        {/* Header with navigation */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <NavLink to={`/zone`}>
              <Button className="ml-2" icon={<ArrowLeftOutlined />} />
            </NavLink>
            <h1 style={{ margin: 0 }}>
              Zones pour le contrat : {zoneDetails?.name}
              <Button
                className="ml-2"
                icon={<EyeOutlined />}
                onClick={() => handleViewEntity(zoneDetails)}
                disabled={isEditing}
              ></Button>
            </h1>
          </Space>
        </div>

        {/* Main content */}
        <Card
          title="Gestion des Zones"
          extra={
            <Space>
              <PermissionRender requiredPermission={["sections", "create"]}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setDrawingMode(true)}
                  disabled={isEditing}
                >
                  Dessiner une nouvelle Zone
                </Button>
              </PermissionRender>
            </Space>
          }
        >
          {/* Map container */}
          <div style={{ height: "50vh", marginBottom: 24 }}>
            <Map
              disableDefaultUI={false}
              options={{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
              }}
              defaultCenter={{ lat: 35.688469, lng: -5.688669 }}
              defaultZoom={11}
              gestureHandling="greedy"
              styles={[
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ]}
            >
              {/* Existing polygons */}

              {zoneDetails && (
                <ExistingPolygons
                  zones={entities}
                  editingZone={editingEntity}
                  onPolygonEdit={handlePolygonEdit}
                  zoomToZone={zoomToEntity}
                  isEditing={true}
                  parentBoundaries={zoneDetails.coordinates}
                />
              )}

              {/* Drawing manager */}
              {drawingMode && (
                <DrawingManager onPolygonComplete={handlePolygonComplete} />
              )}
            </Map>
          </div>

          {/* Sections table */}
          <SectionTable
            sections={entities}
            loading={loading}
            editingEntity={editingEntity}
            isEditing={isEditing}
            onView={handleViewEntity}
            onEdit={startEditingEntity}
            onDelete={handleDelete}
            onSave={openEditModal}
            onCancel={cancelEditing}
          />
        </Card>

        {/* Create/Edit modal */}
        <Modal
          title={
            editingEntity
              ? "Enregistrer les modifications de la zone"
              : "Créer une nouvelle zone"
          }
          open={isModalVisible}
          onOk={form.submit}
          onCancel={handleModalClose}
          width={600}
        >
          <SectionForm form={form} onFinish={handleSave} />
        </Modal>
      </div>
    </APIProvider>
  );
};

export default SectionPage;
